/**
 * ConstructionHoverController — the transient construction hover / crosshair
 * overlay cycle extracted from Scene (#858, epic #828 slice Scene-S4). Scene
 * OWNS a ConstructionHoverController instance and DRIVES it by composition (it
 * is NOT a base class), mirroring the ConstructionOverlayRenderer / MatRenderer
 * / PieceRenderer precedents and the ConstructionTool controller pattern.
 *
 * This controller holds the stateful hover cluster: the marching-ants segment
 * hover, the red delete-hover, the yellow panel-hover, the constraint-hover,
 * the drag-time snap-candidate ring, the free-floating draft-snap-point ring,
 * and the coincident-anchor crosshairs — each a public setter (driven by tools
 * / app / panel) plus a private `refresh*` that (re)builds the overlay, plus
 * the shared per-frame dashOffset loop that animates the marching overlays.
 *
 * Scene stays the public API: it keeps thin delegators for every public setter
 * (~54 external call sites in `src/tools` / `src/ui` / `src/app.ts`), keeps the
 * `render()` orchestration (it calls the controller's `refresh*` in the exact
 * original order after every full scene rebuild), and injects the shared paper
 * scope + live project + construction-line item lookup through the
 * `ConstructionHoverHost` interface. Behaviour is identical to the in-Scene
 * original — this is a pure move + host rewiring.
 */
import paper from "paper";
import { projectConstructionLines, type ConstraintRef } from "../model/Project";
import { projectConstraints } from "../model/constraints";
import {
  getBboxCenter,
  getBboxMidpoint,
  getSegmentMidpoint,
} from "../model/constraintEnforcement";
import { cssVar } from "../colors";
import type { Project } from "../model/Project";
import type { SnapCandidateRef } from "./Scene";

const CONSTRUCTION_STROKE = () => cssVar("--construction-stroke");
/** Ray accent colours, shared with the guide rays (`renderGuideRay`): the
 *  Offset hover affordance uses the same light accent over the mat and the
 *  darker WCAG-contrasting accent over leather (issue #148). */
const SELECTION_STROKE = () => cssVar("--canvas-accent");
const LEATHER_SELECTION_STROKE = () => cssVar("--canvas-accent-leather");

/** The shared Scene state + services the hover overlays reach back into. The
 *  paper scope + overlay layer stay owned by Scene (shared with the piece / mat
 *  / overlay renderers); the live project and the rendered construction-line
 *  item lookup stay single-definition on Scene because non-hover code reads them
 *  too. Implemented by Scene. */
export interface ConstructionHoverHost {
  getPaperScope(): paper.PaperScope;
  getProject(): Project | null;
  getConstructionLineItem(constructionId: string): paper.Item | null;
  /** Whether a canvas point lies over visible leather — picks the Offset
   *  hover affordance's accent (darker over leather for WCAG contrast),
   *  matching the guide rays. */
  isPointOverLeather(pt: { x: number; y: number }): boolean;
}

/** What the Delete sub-sub-tool is hovering, and therefore what its
 *  red overlay traces. Named (rather than repeated inline) because
 *  Scene's delegator, the controller's field and its setter all have to
 *  agree on the shape. */
export type ConstructionDeleteHoverSpec =
  /** Red marching-ants overlay on one curve of the line. */
  | {
      kind: "segment";
      constructionId: string;
      pathIndex: number;
      curveIndex: number;
    }
  /** Hanging-end prune overlay (issue #670): a red marching-ants trace
   *  of just the overhanging stub about to be trimmed. The geometry
   *  comes as ready-made `pathData` rather than indices, since the stub
   *  is a partial slice of a curve, not a whole one. */
  | {
      kind: "prune";
      constructionId: string;
      pathData: string;
    }
  /** One stretch of an offset line (issue #116): the run the source
   *  edge under the cursor generates, which a Delete tap switches off.
   *  Same ready-made-`pathData` overlay as the prune stub — the run is
   *  a slice of the derived geometry, not a whole curve of it — but a
   *  distinct kind so the intent stays readable at the call sites. */
  | {
      kind: "offset-segment";
      constructionId: string;
      pathData: string;
    }
  /** Red ring around an anchor dot. */
  | {
      kind: "anchor";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
    }
  /** Red ring around a tangent endpoint. */
  | {
      kind: "handle";
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
      side: "in" | "out";
    }
  /** Whole-subpath red overlay — used when the cursor hovers a derived
   *  line the gesture can only remove entire (a mirror, or an offset
   *  with no addressable stretch). */
  | {
      kind: "line";
      constructionId: string;
      pathIndex: number;
    };

export class ConstructionHoverController {
  constructor(private host: ConstructionHoverHost) {}

  /** The paper scope is owned by Scene and reached through the host; the hover
   *  overlays draw onto (and probe) the same shared scope. */
  private get scope(): paper.PaperScope {
    return this.host.getPaperScope();
  }

  /** The live project is owned by Scene and reached through the host. */
  private get project(): Project | null {
    return this.host.getProject();
  }

  /** Rendered path for a project-level construction line, via the host. */
  private getConstructionLineItem(constructionId: string): paper.Item | null {
    return this.host.getConstructionLineItem(constructionId);
  }

  /** Currently-hovered construction segment from the properties
   *  panel's segment list (or null when nothing is hovered). Drives
   *  the marching-ants overlay drawn on top of the matching curve
   *  so the user can connect each row in the list to its segment on
   *  the canvas at a glance. */
  private constructionSegmentHover: {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
  } | null = null;
  private constructionHoverItem: paper.Path | null = null;
  private constructionHoverFrameInstalled = false;
  /** Currently-hovered delete-sub-sub-tool target, populated by
   *  `ConstructionTool` while the cursor is over a deletable thing
   *  (curve / anchor / tangent dot) in delete mode. Drives a red
   *  overlay on the matching geometry. */
  private constructionDeleteHover: ConstructionDeleteHoverSpec | null = null;
  private constructionDeleteHoverItem: paper.Item | null = null;
  /** Currently-hovered segment-list dot, populated by
   *  `PropertiesPanel` while the cursor is over an indicator dot.
   *  Drives a yellow overlay on the matching anchor + handle on
   *  the canvas (parallel to the red delete-hover, but yellow and
   *  always includes the anchor). */
  private constructionPanelHover:
    | {
        kind: "anchor";
        constructionId: string;
        pathIndex: number;
        segmentIndex: number;
      }
    | {
        kind: "handle";
        constructionId: string;
        pathIndex: number;
        segmentIndex: number;
        side: "in" | "out";
      }
    | {
        kind: "both-handles";
        constructionId: string;
        pathIndex: number;
        segmentIndex: number;
      }
    | null = null;
  private constructionPanelHoverItem: paper.Item | null = null;
  /** Hover-overlay state for an entire constraint — surfaces all
   *  the features the constraint references at once (yellow ring
   *  per anchor, yellow marching-ants per curve, yellow halo per
   *  whole line). Driven by the panel + layers chip mouseenter /
   *  mouseleave. */
  private constructionConstraintHover: {
    refs: ConstraintRef[];
  } | null = null;
  private constructionConstraintHoverItem: paper.Item | null = null;
  /** Drag-time snap-candidate highlight — the source anchor + the
   *  about-to-snap target anchor light up while the user holds a
   *  vertex / line drag near another line's anchor. Cleared on
   *  release or when the cursor leaves the snap radius. */
  private constructionSnapCandidate: {
    /** Source is omitted when the gesture isn't a drag on an
     *  existing anchor / centre / midpoint — e.g., a brand-new shape
     *  being drawn whose draft cursor has no on-scene anchor yet.
     *  Both source and candidate are the same anchor-or-centre-or-
     *  midpoint union: a whole-line drag of a primitive offers each
     *  of those as a source so centre-to-centre and midpoint snaps
     *  work too. */
    source?: SnapCandidateRef;
    candidate: SnapCandidateRef;
  } | null = null;
  private constructionSnapCandidateItem: paper.Item | null = null;
  /** Free-floating snap target shown during draw-mode hover (issue
   *  follow-up to #259): when the cursor is in Construction Draw
   *  mode and would snap to a point that doesn't belong to any
   *  existing line (typically a grid intersection), this carries
   *  the target position in project mm so the next render paints a
   *  yellow ring at that exact spot. Distinct from
   *  `constructionSnapCandidate` — that one is keyed to an existing
   *  anchor / centre / midpoint ref; this one is a bare point. */
  private constructionDraftSnapPoint: { x: number; y: number } | null = null;
  private constructionDraftSnapPointItem: paper.Item | null = null;
  /** When `true`, persistent crosshair markers are drawn over every
   *  pair of anchors pinned by a coincident constraint. Toggled by
   *  the Construction tool on activation / deactivation. */
  private constructionCrosshairsVisible = false;
  private constructionCrosshairsItem: paper.Item | null = null;
  /** Offset hover affordance (issue #148): a small screen-constant
   *  arrow drawn next to the cursor while it hovers an edge an Offset
   *  tool can offset from — the construction Offset sub-tool's lines,
   *  and the leather Stitch tool's part / cutout / hole edges (#768).
   *  The arrow points perpendicularly away from the edge in `dir`, with
   *  a tick parallel to the edge (`tan`) at its tip standing in for the
   *  offset line that a drag would create. The tool computes the
   *  geometry (foot / side / tangent) and hands us resolved unit
   *  vectors; we just paint the glyph. */
  private offsetHoverAffordance: {
    x: number;
    y: number;
    dirX: number;
    dirY: number;
    tanX: number;
    tanY: number;
  } | null = null;
  private offsetHoverAffordanceItem: paper.Item | null = null;
  /** Where the bezier-toggle glyph is anchored (#147), or null. */
  private bezierToggleAffordance: { x: number; y: number } | null = null;
  private bezierToggleAffordanceItem: paper.Item | null = null;

  /** Set / clear the currently-hovered construction segment. Driven
   *  by the properties panel's segment list (mouseenter / mouseleave
   *  on each row) — the matching curve segment on the canvas gets a
   *  marching-ants overlay so the user can find it at a glance. */
  setConstructionSegmentHover(
    spec: {
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
    } | null,
  ): void {
    this.constructionSegmentHover = spec;
    this.refreshConstructionHover();
  }

  /** Build (or rebuild) the marching-ants overlay path for whichever
   *  construction segment the panel is currently hovering. Called
   *  from `setConstructionSegmentHover` (when the hover changes)
   *  and from the end of `render` (so the overlay survives the full
   *  scene rebuild that fires on every history change). */
  refreshConstructionHover(): void {
    if (this.constructionHoverItem) {
      this.constructionHoverItem.remove();
    }
    this.constructionHoverItem = null;
    if (!this.constructionSegmentHover) {
      this.releaseConstructionHoverFrameLoopIfIdle();
      return;
    }
    const { constructionId, pathIndex, segmentIndex } =
      this.constructionSegmentHover;
    const item = this.getConstructionLineItem(constructionId);
    if (!item) return;
    const cp = item as paper.CompoundPath;
    const subpaths: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [item as unknown as paper.Path];
    const sub = subpaths[pathIndex];
    if (!sub) return;
    const curve = sub.curves[segmentIndex];
    if (!curve) return;
    // Build the overlay as an explicit cubic so it tracks the live
    // shape regardless of whether the underlying segment carries
    // bezier handles.
    this.scope.activate();
    const overlay = new this.scope.Path();
    overlay.data = { kind: "construction-hover" };
    overlay.moveTo(curve.point1);
    overlay.cubicCurveTo(
      curve.point1.add(curve.handle1),
      curve.point2.add(curve.handle2),
      curve.point2,
    );
    overlay.strokeColor = new this.scope.Color(CONSTRUCTION_STROKE());
    overlay.strokeWidth = 2.4;
    overlay.strokeScaling = false;
    overlay.fillColor = null;
    // 6 / 4 mm dash gives a clearly readable rhythm; dashOffset
    // gets driven by the onFrame loop below.
    overlay.dashArray = [6, 4];
    overlay.dashOffset = 0;
    // Mark ephemeral so the hit-test skips it. Otherwise, when the
    // overlay is on top of the construction line, the cursor lands
    // on the overlay → hitTest finds no recognised kind → cursor
    // reads "default" → overlay clears → next mouse move picks the
    // line again → overlay re-adds → flicker.
    overlay.data = { kind: "construction-hover", ephemeral: true };
    this.constructionHoverItem = overlay;
    this.ensureConstructionHoverFrameLoop();
  }

  /** Install the per-frame dashOffset tick. Paper.js's `view.onFrame`
   *  setter starts an internal `requestAnimationFrame` loop that
   *  keeps firing until the handler is cleared, so we install lazily
   *  on the first overlay and tear down via
   *  `releaseConstructionHoverFrameLoopIfIdle` once all three overlay
   *  refs are null again (issue #194). */
  private ensureConstructionHoverFrameLoop(): void {
    if (this.constructionHoverFrameInstalled) return;
    this.constructionHoverFrameInstalled = true;
    this.scope.view.onFrame = (event: { time: number }) => {
      // ~10 mm per second crawl, in the curve's positive direction.
      const offset = -event.time * 10;
      if (this.constructionHoverItem) {
        this.constructionHoverItem.dashOffset = offset;
      }
      // Delete-segment / handle / anchor overlays each have their
      // own animation expectation:
      //   - segment + whole-line dashed paths march via dashOffset.
      //   - anchor / handle solid circles don't dash — writes are
      //     harmless no-ops.
      //   - whole-line overlay is a Group with a static halo + a
      //     dashed marching-ants child; iterate children so the
      //     marching child animates and the halo stays still.
      const del = this.constructionDeleteHoverItem;
      if (del) {
        const grp = del as paper.Group;
        if (grp.children && grp.children.length > 0) {
          for (const child of grp.children) {
            const p = child as paper.Path;
            if (p.dashArray && p.dashArray.length > 0) {
              p.dashOffset = offset;
            }
          }
        } else {
          const p = del as paper.Path;
          if (p.dashArray && p.dashArray.length > 0) {
            p.dashOffset = offset;
          }
        }
      }
      // Constraint-hover group can hold mixed children — animate
      // each Path child that has a dash array (the curve refs).
      const grp = this.constructionConstraintHoverItem;
      if (grp && grp.children) {
        for (const child of grp.children) {
          const p = child as paper.Path;
          if (p.dashArray && p.dashArray.length > 0) {
            p.dashOffset = offset;
          }
        }
      }
    };
  }

  /** Tear down the per-frame dashOffset tick when none of the three
   *  marching-ants overlays are present. Clearing `view.onFrame`
   *  triggers Paper.js's internal `view.pause()` which sets
   *  `_animate = false`, so the self-recurring `requestAnimationFrame`
   *  stops on its next tick. Re-installed lazily by
   *  `ensureConstructionHoverFrameLoop` on the next hover that needs
   *  it. */
  private releaseConstructionHoverFrameLoopIfIdle(): void {
    if (!this.constructionHoverFrameInstalled) return;
    if (
      this.constructionHoverItem ||
      this.constructionDeleteHoverItem ||
      this.constructionConstraintHoverItem
    ) {
      return;
    }
    this.scope.view.onFrame = null;
    this.constructionHoverFrameInstalled = false;
  }

  /** Set / clear the currently-hovered segment-list dot. Parallel
   *  to `setConstructionDeleteHover` but renders a yellow overlay
   *  (cycle-mode color) and always includes a ring around the
   *  affected anchor — the user expects the dot hover to surface
   *  the specific anchor + its handle on the canvas, not the
   *  entire curve segment the row represents. */
  setConstructionPanelHover(
    spec:
      | {
          kind: "anchor";
          constructionId: string;
          pathIndex: number;
          segmentIndex: number;
        }
      | {
          kind: "handle";
          constructionId: string;
          pathIndex: number;
          segmentIndex: number;
          side: "in" | "out";
        }
      | {
          kind: "both-handles";
          constructionId: string;
          pathIndex: number;
          segmentIndex: number;
        }
      | null,
  ): void {
    this.constructionPanelHover = spec;
    this.refreshConstructionPanelHover();
  }

  /** Build (or rebuild) the yellow overlay for the panel-hovered
   *  anchor / handle. Called from `setConstructionPanelHover` and
   *  from the end of `render` so the overlay survives full scene
   *  rebuilds. */
  refreshConstructionPanelHover(): void {
    if (this.constructionPanelHoverItem) {
      this.constructionPanelHoverItem.remove();
    }
    this.constructionPanelHoverItem = null;
    const spec = this.constructionPanelHover;
    if (!spec) return;
    const item = this.getConstructionLineItem(spec.constructionId);
    if (!item) return;
    const cp = item as paper.CompoundPath;
    const subpaths: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [item as unknown as paper.Path];
    const sub = subpaths[spec.pathIndex];
    if (!sub) return;
    const seg = sub.segments[spec.segmentIndex];
    if (!seg) return;
    this.scope.activate();
    const YELLOW = new this.scope.Color(CONSTRUCTION_STROKE());
    const HOVER_DATA = { kind: "construction-panel-hover", ephemeral: true };
    const group = new this.scope.Group();
    group.data = { ...HOVER_DATA };
    // Anchor ring — always drawn so the user sees which anchor
    // the dot represents.
    const anchorRing = new this.scope.Path.Circle(seg.point, 1.8);
    const anchorFill = new this.scope.Color(CONSTRUCTION_STROKE());
    anchorFill.alpha = 0.35;
    anchorRing.fillColor = anchorFill;
    anchorRing.strokeColor = YELLOW;
    anchorRing.strokeWidth = 1.8;
    anchorRing.strokeScaling = false;
    anchorRing.data = { ...HOVER_DATA };
    group.addChild(anchorRing);
    const drawSide = (side: "in" | "out") => {
      const handle = side === "in" ? seg.handleIn : seg.handleOut;
      if (handle.isZero()) return;
      const tip = seg.point.add(handle);
      const tangent = new this.scope.Path({ segments: [seg.point, tip] });
      tangent.strokeColor = YELLOW;
      tangent.strokeWidth = 2.0;
      tangent.strokeScaling = false;
      tangent.fillColor = null;
      tangent.data = { ...HOVER_DATA };
      group.addChild(tangent);
      const tipDot = new this.scope.Path.Circle(tip, 1.4);
      const tipFill = new this.scope.Color(CONSTRUCTION_STROKE());
      tipFill.alpha = 0.35;
      tipDot.fillColor = tipFill;
      tipDot.strokeColor = YELLOW;
      tipDot.strokeWidth = 1.4;
      tipDot.strokeScaling = false;
      tipDot.data = { ...HOVER_DATA };
      group.addChild(tipDot);
    };
    if (spec.kind === "handle") {
      drawSide(spec.side);
    } else if (spec.kind === "both-handles") {
      // Bridge hover — light up both sides of the (locked) anchor.
      drawSide("in");
      drawSide("out");
    }
    this.constructionPanelHoverItem = group;
  }

  /** Set / clear the constraint currently being hovered in the
   *  properties / layers panel. Surfaces every feature the
   *  constraint references at once (yellow ring per anchor,
   *  marching-ants per curve, halo per whole line) so the user
   *  can see "this constraint connects these things". */
  setConstructionConstraintHover(spec: { refs: ConstraintRef[] } | null): void {
    this.constructionConstraintHover = spec;
    this.refreshConstructionConstraintHover();
  }

  /** Build (or rebuild) the yellow overlay group for the panel-
   *  hovered constraint. Called from `setConstructionConstraintHover`
   *  and from the end of `render` so the overlay survives full
   *  scene rebuilds. */
  refreshConstructionConstraintHover(): void {
    if (this.constructionConstraintHoverItem) {
      this.constructionConstraintHoverItem.remove();
    }
    this.constructionConstraintHoverItem = null;
    const spec = this.constructionConstraintHover;
    if (!spec) {
      this.releaseConstructionHoverFrameLoopIfIdle();
      return;
    }
    this.scope.activate();
    const YELLOW = new this.scope.Color(CONSTRUCTION_STROKE());
    const HOVER_DATA = {
      kind: "construction-constraint-hover",
      ephemeral: true,
    };
    const group = new this.scope.Group();
    group.data = { ...HOVER_DATA };
    for (const ref of spec.refs) {
      const item = this.getConstructionLineItem(ref.constructionId);
      if (!item) continue;
      const cp = item as paper.CompoundPath;
      const subpaths: paper.Path[] =
        cp.children && cp.children.length > 0
          ? (cp.children as paper.Path[])
          : [item as unknown as paper.Path];
      if (ref.kind === "line") {
        // Halo over the whole line — wider faded stroke under the
        // existing yellow.
        for (const sub of subpaths) {
          const halo = sub.clone({ insert: false }) as paper.Path;
          halo.strokeColor = YELLOW;
          halo.strokeColor.alpha = 0.35;
          halo.strokeWidth = 6.0;
          halo.strokeScaling = false;
          halo.fillColor = null;
          halo.dashArray = [];
          // Tag the halo with its source line id so callers (tests,
          // future hit-test work) can pair halos with lines without
          // a position lookup.
          halo.data = { ...HOVER_DATA, constructionId: ref.constructionId };
          group.addChild(halo);
        }
        continue;
      }
      if (ref.kind === "center" || ref.kind === "bbox-midpoint") {
        // Ring on the bbox centre or one of the 4 edge midpoints of
        // a primitive line. Looked up from the model (not the
        // rendered item) because the position is derived from
        // primitive metadata.
        const line = projectConstructionLines(this.project!).find(
          (l) => l.id === ref.constructionId,
        );
        const pos = !line
          ? null
          : ref.kind === "center"
            ? getBboxCenter(line, this.scope)
            : getBboxMidpoint(line, ref.edge, this.scope);
        if (!pos) continue;
        const ring = new this.scope.Path.Circle(
          new this.scope.Point(pos.x, pos.y),
          1.8,
        );
        const fill = new this.scope.Color(CONSTRUCTION_STROKE());
        fill.alpha = 0.35;
        ring.fillColor = fill;
        ring.strokeColor = YELLOW;
        ring.strokeWidth = 1.8;
        ring.strokeScaling = false;
        ring.data = { ...HOVER_DATA };
        group.addChild(ring);
        continue;
      }
      const sub = subpaths[ref.pathIndex];
      if (!sub) continue;
      if (ref.kind === "anchor") {
        const seg = sub.segments[ref.segmentIndex];
        if (!seg) continue;
        const ring = new this.scope.Path.Circle(seg.point, 1.8);
        const fill = new this.scope.Color(CONSTRUCTION_STROKE());
        fill.alpha = 0.35;
        ring.fillColor = fill;
        ring.strokeColor = YELLOW;
        ring.strokeWidth = 1.8;
        ring.strokeScaling = false;
        ring.data = { ...HOVER_DATA };
        group.addChild(ring);
        // Highlight the two curve segments adjacent to this anchor
        // too, so the hover signal reads "this constraint pins
        // *this point* on *this curve*", not just the bare dot.
        // paper.Segment.curve is the outgoing curve (segments[i] →
        // segments[i+1]); the incoming side is curves[i-1]. Open
        // endpoints contribute only one side; closed subpaths wrap
        // both. Same marching-ants visual as the curve ref kind so
        // the two ref types read consistently.
        const adjacentCurves: paper.Curve[] = [];
        const outgoing = sub.curves[ref.segmentIndex];
        if (outgoing) adjacentCurves.push(outgoing);
        if (ref.segmentIndex > 0) {
          const incoming = sub.curves[ref.segmentIndex - 1];
          if (incoming) adjacentCurves.push(incoming);
        } else if (sub.closed && sub.curves.length > 0) {
          // Wrap-around incoming curve for a closed subpath's first
          // segment.
          const wrap = sub.curves[sub.curves.length - 1];
          if (wrap) adjacentCurves.push(wrap);
        }
        for (const curve of adjacentCurves) {
          const overlay = new this.scope.Path();
          overlay.moveTo(curve.point1);
          overlay.cubicCurveTo(
            curve.point1.add(curve.handle1),
            curve.point2.add(curve.handle2),
            curve.point2,
          );
          overlay.strokeColor = YELLOW;
          overlay.strokeWidth = 2.6;
          overlay.strokeScaling = false;
          overlay.fillColor = null;
          overlay.dashArray = [6, 4];
          overlay.dashOffset = 0;
          overlay.data = { ...HOVER_DATA };
          group.addChild(overlay);
        }
        continue;
      }
      // curve
      const curve = sub.curves[ref.curveIndex];
      if (!curve) continue;
      const overlay = new this.scope.Path();
      overlay.moveTo(curve.point1);
      overlay.cubicCurveTo(
        curve.point1.add(curve.handle1),
        curve.point2.add(curve.handle2),
        curve.point2,
      );
      overlay.strokeColor = YELLOW;
      overlay.strokeWidth = 2.6;
      overlay.strokeScaling = false;
      overlay.fillColor = null;
      overlay.dashArray = [6, 4];
      overlay.dashOffset = 0;
      overlay.data = { ...HOVER_DATA };
      group.addChild(overlay);
    }
    this.constructionConstraintHoverItem = group;
    this.ensureConstructionHoverFrameLoop();
  }

  /** Set / clear the drag-time snap-candidate highlight. Drawn as
   *  a yellow ring on each of the two anchors (source + candidate)
   *  to flag that releasing the drag will pin them with a
   *  coincident constraint. Driven by the Construction tool's
   *  per-frame snap check. */
  setConstructionSnapCandidateHover(
    spec: {
      source?: SnapCandidateRef;
      candidate: SnapCandidateRef;
    } | null,
  ): void {
    this.constructionSnapCandidate = spec;
    this.refreshConstructionSnapCandidate();
  }

  refreshConstructionSnapCandidate(): void {
    if (this.constructionSnapCandidateItem) {
      this.constructionSnapCandidateItem.remove();
    }
    this.constructionSnapCandidateItem = null;
    const spec = this.constructionSnapCandidate;
    if (!spec) return;
    this.scope.activate();
    const YELLOW = new this.scope.Color(CONSTRUCTION_STROKE());
    const group = new this.scope.Group();
    group.data = { kind: "construction-snap-candidate", ephemeral: true };
    const ringAt = (pos: { x: number; y: number } | null) => {
      if (!pos) return;
      const ring = new this.scope.Path.Circle(
        new this.scope.Point(pos.x, pos.y),
        2.0,
      );
      const fill = new this.scope.Color(CONSTRUCTION_STROKE());
      fill.alpha = 0.45;
      ring.fillColor = fill;
      ring.strokeColor = YELLOW;
      ring.strokeWidth = 2.2;
      ring.strokeScaling = false;
      ring.data = { kind: "construction-snap-candidate", ephemeral: true };
      group.addChild(ring);
    };
    const anchorPos = (ref: {
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
    }): { x: number; y: number } | null => {
      const item = this.getConstructionLineItem(ref.constructionId);
      if (!item) return null;
      const cp = item as paper.CompoundPath;
      const subpaths: paper.Path[] =
        cp.children && cp.children.length > 0
          ? (cp.children as paper.Path[])
          : [item as unknown as paper.Path];
      const sub = subpaths[ref.pathIndex];
      if (!sub) return null;
      const seg = sub.segments[ref.segmentIndex];
      if (!seg) return null;
      return { x: seg.point.x, y: seg.point.y };
    };
    const refPos = (ref: SnapCandidateRef): { x: number; y: number } | null => {
      if (ref.kind === "anchor") {
        return anchorPos({
          constructionId: ref.constructionId,
          pathIndex: ref.pathIndex,
          segmentIndex: ref.segmentIndex,
        });
      }
      if (ref.kind === "segment-midpoint") {
        // Midpoint between this anchor and the next (wrap if closed) —
        // read from the LIVE rendered item so the ring tracks drags,
        // with the model pathData as the first-paint fallback.
        const liveItem = this.getConstructionLineItem(ref.constructionId);
        if (liveItem) {
          const cp = liveItem as paper.CompoundPath;
          const subs =
            cp.children && cp.children.length > 0
              ? (cp.children as paper.Path[])
              : [liveItem as unknown as paper.Path];
          const sub = subs[ref.pathIndex];
          const a = sub?.segments[ref.segmentIndex];
          const nextIndex =
            sub && ref.segmentIndex + 1 < sub.segments.length
              ? ref.segmentIndex + 1
              : sub?.closed
                ? 0
                : -1;
          const b = nextIndex >= 0 ? sub?.segments[nextIndex] : undefined;
          if (a && b) {
            return {
              x: (a.point.x + b.point.x) / 2,
              y: (a.point.y + b.point.y) / 2,
            };
          }
        }
        const ln = this.project
          ? projectConstructionLines(this.project).find(
              (l) => l.id === ref.constructionId,
            )
          : undefined;
        return ln
          ? getSegmentMidpoint(
              ln.pathData,
              ref.pathIndex,
              ref.segmentIndex,
              this.scope,
            )
          : null;
      }
      // Centre / midpoint refs: read the LIVE rendered item's
      // bounds so the ring tracks per-frame drags. Model primitive
      // metadata is only the commit-time fallback (used when the
      // item hasn't rendered yet, e.g. on first paint).
      const item = this.getConstructionLineItem(ref.constructionId);
      if (item) {
        const b = item.bounds;
        if (ref.kind === "center") {
          return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
        }
        const cx = b.x + b.width / 2;
        const cy = b.y + b.height / 2;
        switch (ref.edge) {
          case "top":
            return { x: cx, y: b.y };
          case "bottom":
            return { x: cx, y: b.y + b.height };
          case "left":
            return { x: b.x, y: cy };
          case "right":
            return { x: b.x + b.width, y: cy };
        }
      }
      const line = this.project
        ? projectConstructionLines(this.project).find(
            (l) => l.id === ref.constructionId,
          )
        : undefined;
      if (!line) return null;
      return ref.kind === "center"
        ? getBboxCenter(line, this.scope)
        : getBboxMidpoint(line, ref.edge, this.scope);
    };
    if (spec.source) ringAt(refPos(spec.source));
    ringAt(refPos(spec.candidate));
    this.constructionSnapCandidateItem = group;
  }

  /** Show (or hide) a free-floating yellow snap target at an
   *  arbitrary project-mm point. Used by the Construction Draw
   *  sub-mode to preview where a click would land when grid snap
   *  fires on empty space (no nearby anchor). Pass `null` to clear.
   *  Mutually exclusive with the anchor / centre / midpoint
   *  candidate ring — only one should be lit at a time. */
  setConstructionDraftSnapPoint(pt: { x: number; y: number } | null): void {
    this.constructionDraftSnapPoint = pt;
    this.refreshConstructionDraftSnapPoint();
  }

  /** Inspect the current free-floating draft snap target — used by
   *  the e2e suite to verify the hover ring landed at the expected
   *  grid intersection without scraping the rendered Paper items. */
  getConstructionDraftSnapPoint(): { x: number; y: number } | null {
    return this.constructionDraftSnapPoint;
  }

  private refreshConstructionDraftSnapPoint(): void {
    if (this.constructionDraftSnapPointItem) {
      this.constructionDraftSnapPointItem.remove();
    }
    this.constructionDraftSnapPointItem = null;
    const pt = this.constructionDraftSnapPoint;
    if (!pt) return;
    this.scope.activate();
    const ring = new this.scope.Path.Circle(
      new this.scope.Point(pt.x, pt.y),
      2.0,
    );
    const fill = new this.scope.Color(CONSTRUCTION_STROKE());
    fill.alpha = 0.45;
    ring.fillColor = fill;
    ring.strokeColor = new this.scope.Color(CONSTRUCTION_STROKE());
    ring.strokeWidth = 2.2;
    ring.strokeScaling = false;
    ring.data = { kind: "construction-draft-snap-point", ephemeral: true };
    this.constructionDraftSnapPointItem = ring;
  }

  /** Toggle the persistent crosshair markers that sit over every
   *  pair of anchors pinned by a coincident (Snap) constraint —
   *  one crosshair per pair, drawn at the pair's shared position.
   *  Called by the Construction tool on activation / deactivation
   *  so the markers stay scoped to the tool that cares. */
  setConstructionCrosshairsVisible(visible: boolean): void {
    if (this.constructionCrosshairsVisible === visible) return;
    this.constructionCrosshairsVisible = visible;
    this.refreshConstructionCrosshairs();
  }

  /** Force a crosshair rebuild from the *live* rendered geometry —
   *  called per-frame during a Construction drag so the markers
   *  travel with the rigidly-translated partner lines instead of
   *  staying frozen until commit. */
  refreshConstructionCrosshairsLive(): void {
    if (!this.constructionCrosshairsVisible) return;
    this.refreshConstructionCrosshairs();
  }

  refreshConstructionCrosshairs(): void {
    if (this.constructionCrosshairsItem) {
      this.constructionCrosshairsItem.remove();
    }
    this.constructionCrosshairsItem = null;
    if (!this.constructionCrosshairsVisible) return;
    const proj = this.project;
    if (!proj) return;
    this.scope.activate();
    const group = new this.scope.Group();
    group.data = { kind: "construction-crosshairs", ephemeral: true };
    const YELLOW = new this.scope.Color(CONSTRUCTION_STROKE());
    // Walk every coincident constraint and compute the LIVE position
    // of each ref. A constraint is satisfied when every ref ends up
    // at the same point within `epsilonMm`; otherwise it's broken and
    // we paint a red-dot crosshair at every ref so the user can see
    // both ends of the gap. Mirrors the Snap palette icon so the
    // on-canvas marker reads as "this is the Snap constraint."
    const lines = proj.constructionLines ?? [];
    // Live drags can leave positions a fraction of a mm apart due to
    // float rounding; use the same 0.001 mm tolerance as
    // `findBrokenCoincidentConstraints` so the visual flag matches
    // the model's own broken-check.
    const EPSILON_MM = 0.001;
    const refPos = (ref: ConstraintRef): { x: number; y: number } | null => {
      if (ref.kind === "anchor") {
        const item = this.getConstructionLineItem(ref.constructionId);
        if (!item) return null;
        const cp = item as paper.CompoundPath;
        const subpaths: paper.Path[] =
          cp.children && cp.children.length > 0
            ? (cp.children as paper.Path[])
            : [item as unknown as paper.Path];
        const sub = subpaths[ref.pathIndex];
        if (!sub) return null;
        const seg = sub.segments[ref.segmentIndex];
        if (!seg) return null;
        return { x: seg.point.x, y: seg.point.y };
      }
      if (ref.kind === "center" || ref.kind === "bbox-midpoint") {
        // Read the centre / midpoint from the LIVE rendered item's
        // bounds so it tracks per-frame translations during a drag.
        // Falling back to the model's primitive would freeze the
        // crosshair at the pre-drag position until commit.
        const line = lines.find((l) => l.id === ref.constructionId);
        if (!line) return null;
        const item = this.getConstructionLineItem(ref.constructionId);
        if (item) {
          const b = item.bounds;
          if (ref.kind === "center") {
            return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
          }
          const mcx = b.x + b.width / 2;
          const mcy = b.y + b.height / 2;
          switch (ref.edge) {
            case "top":
              return { x: mcx, y: b.y };
            case "bottom":
              return { x: mcx, y: b.y + b.height };
            case "left":
              return { x: b.x, y: mcy };
            case "right":
              return { x: b.x + b.width, y: mcy };
          }
        }
        return ref.kind === "center"
          ? getBboxCenter(line, this.scope)
          : getBboxMidpoint(line, ref.edge, this.scope);
      }
      return null;
    };
    // Arm length is fixed in project mm so the crosshair stays
    // legible at modest zoom; strokeScaling = false keeps the
    // visual weight stable across zoom levels. Arm reaches just
    // past the 1.1 mm anchor-dot radius so the crosshair reads
    // as anchored to the dot without dwarfing it.
    const arm = 1.9;
    const buildCrosshair = (
      pos: { x: number; y: number },
      broken: boolean,
      constraintId: string,
    ): void => {
      const dataKind = broken
        ? "construction-crosshairs-broken"
        : "construction-crosshairs";
      const v = new this.scope.Path({
        segments: [
          new this.scope.Point(pos.x, pos.y - arm),
          new this.scope.Point(pos.x, pos.y + arm),
        ],
      });
      v.strokeColor = YELLOW;
      v.strokeColor.alpha = 0.85;
      v.strokeWidth = 1.4;
      v.strokeScaling = false;
      v.data = { kind: dataKind, ephemeral: true, constraintId };
      group.addChild(v);
      const h = new this.scope.Path({
        segments: [
          new this.scope.Point(pos.x - arm, pos.y),
          new this.scope.Point(pos.x + arm, pos.y),
        ],
      });
      h.strokeColor = YELLOW;
      h.strokeColor.alpha = 0.85;
      h.strokeWidth = 1.4;
      h.strokeScaling = false;
      h.data = { kind: dataKind, ephemeral: true, constraintId };
      group.addChild(h);
      const dot = new this.scope.Path.Circle(
        new this.scope.Point(pos.x, pos.y),
        0.6,
      );
      // White dot when satisfied (current look); red when the pair
      // has drifted apart so the user notices the broken constraint
      // at a glance. Same dot size + outline so the visual weight
      // doesn't shift between states.
      dot.fillColor = broken
        ? new this.scope.Color(0.95, 0.1, 0.1)
        : new this.scope.Color(1, 1, 1);
      dot.strokeColor = null;
      dot.data = { kind: dataKind, ephemeral: true, constraintId };
      group.addChild(dot);
    };
    for (const c of projectConstraints(proj)) {
      if (c.kind !== "coincident") continue;
      const positions: Array<{ x: number; y: number }> = [];
      for (const ref of c.refs) {
        const pos = refPos(ref);
        if (pos) positions.push(pos);
      }
      if (positions.length === 0) continue;
      const first = positions[0]!;
      const broken =
        positions.length >= 2 &&
        positions.some(
          (p) => Math.hypot(p.x - first.x, p.y - first.y) > EPSILON_MM,
        );
      if (broken) {
        // Render one crosshair per ref so the user sees both ends of
        // the gap. Each gets the red centre dot.
        for (const p of positions) buildCrosshair(p, true, c.id);
      } else {
        buildCrosshair(first, false, c.id);
      }
    }
    this.constructionCrosshairsItem = group;
  }

  /** Set / clear the Offset hover affordance (issue #148). Driven by
   *  the construction Offset sub-tool (`refreshEditCursor`) and the
   *  leather Stitch tool (`prePressHover`, #768): non-null (with the
   *  cursor anchor + the resolved perpendicular `dir` / parallel `tan`
   *  unit vectors) when the cursor is over an offsettable edge, `null`
   *  otherwise. */
  setOffsetHoverAffordance(
    spec: {
      x: number;
      y: number;
      dirX: number;
      dirY: number;
      tanX: number;
      tanY: number;
    } | null,
  ): void {
    // Cheap no-op guard: hover fires on every pointer move, so skip the
    // paper rebuild when nothing meaningful changed (both null, or the
    // same anchor + orientation within a sub-pixel epsilon).
    const cur = this.offsetHoverAffordance;
    if (spec === null && cur === null) return;
    if (
      spec &&
      cur &&
      Math.abs(spec.x - cur.x) < 1e-4 &&
      Math.abs(spec.y - cur.y) < 1e-4 &&
      Math.abs(spec.dirX - cur.dirX) < 1e-4 &&
      Math.abs(spec.dirY - cur.dirY) < 1e-4 &&
      Math.abs(spec.tanX - cur.tanX) < 1e-4 &&
      Math.abs(spec.tanY - cur.tanY) < 1e-4
    ) {
      return;
    }
    this.offsetHoverAffordance = spec;
    this.refreshOffsetHoverAffordance();
  }

  /** Build (or rebuild) the Offset hover glyph: a screen-constant
   *  arrow starting just off the cursor, pointing away from the edge
   *  along `dir`, capped with an arrowhead and a tick parallel to the
   *  edge (the offset-line indicator). Called from
   *  `setOffsetHoverAffordance` and from the end of `render` so the
   *  glyph survives full scene rebuilds. */
  refreshOffsetHoverAffordance(): void {
    if (this.offsetHoverAffordanceItem) {
      this.offsetHoverAffordanceItem.remove();
    }
    this.offsetHoverAffordanceItem = null;
    const spec = this.offsetHoverAffordance;
    if (!spec) return;
    this.scope.activate();
    const zoom = this.scope.view.zoom || 1;
    const P = this.scope.Point;
    // Match the guide rays: darker accent over leather (WCAG contrast against
    // the pale fill), light accent over the mat. The cursor anchor alone flips
    // as you graze the edge — hovering just OUTSIDE a part reads as "on the
    // mat" even though the offset targets the leather — so also probe a short
    // way along `dir` (which always points toward the leather the seam insets
    // into). Either landing on leather picks the leather accent. The probe is
    // zoom-scaled to clear the edge grab band (StitchTool's 6 px / zoom), so it
    // always crosses onto leather regardless of zoom. (In the construction
    // Offset tool `isPointOverLeather` is always false, so both stay light.)
    const probeMm = 10 / zoom;
    const overLeather =
      this.host.isPointOverLeather({ x: spec.x, y: spec.y }) ||
      this.host.isPointOverLeather({
        x: spec.x + spec.dirX * probeMm,
        y: spec.y + spec.dirY * probeMm,
      });
    const color = new this.scope.Color(
      overLeather ? LEATHER_SELECTION_STROKE() : SELECTION_STROKE(),
    );
    // Screen-constant lengths (project mm = px / zoom), so the glyph
    // stays the same visual size at every zoom level.
    const gap = 7 / zoom; // start off the pointer's hotspot
    const shaft = 20 / zoom; // arrow length
    const ah = 6 / zoom; // arrowhead length
    const aw = 4 / zoom; // arrowhead half-width
    const tick = 9 / zoom; // half-width of the offset-line tick
    const dir = new P(spec.dirX, spec.dirY);
    const tan = new P(spec.tanX, spec.tanY);
    const base = new P(spec.x, spec.y).add(dir.multiply(gap));
    const tip = base.add(dir.multiply(shaft));
    const group = new this.scope.Group();
    group.data = { kind: "offset-hover-affordance", ephemeral: true };
    // Shaft.
    const shaftLine = new this.scope.Path({ segments: [base, tip] });
    shaftLine.strokeColor = color;
    shaftLine.strokeWidth = 1.6;
    shaftLine.strokeScaling = false;
    shaftLine.data = { kind: "offset-hover-affordance", ephemeral: true };
    group.addChild(shaftLine);
    // Arrowhead — a filled chevron with its vertex at the tip.
    const backCenter = tip.subtract(dir.multiply(ah));
    const head = new this.scope.Path([
      tip,
      backCenter.add(tan.multiply(aw)),
      backCenter.subtract(tan.multiply(aw)),
    ]);
    head.closed = true;
    head.fillColor = color;
    head.data = { kind: "offset-hover-affordance", ephemeral: true };
    group.addChild(head);
    // Offset-line tick at the tip, parallel to the hovered edge.
    const tickLine = new this.scope.Path({
      segments: [tip.subtract(tan.multiply(tick)), tip.add(tan.multiply(tick))],
    });
    tickLine.strokeColor = color;
    tickLine.strokeWidth = 1.6;
    tickLine.strokeScaling = false;
    tickLine.data = { kind: "offset-hover-affordance", ephemeral: true };
    group.addChild(tickLine);
    this.offsetHoverAffordanceItem = group;
  }

  /** Set / clear the bezier-toggle hover affordance (#147) — the glyph drawn
   *  next to the cursor while it hovers a point that a click would toggle
   *  between a corner and a curve. Pass `null` to clear. */
  setBezierToggleAffordance(pt: { x: number; y: number } | null): void {
    const cur = this.bezierToggleAffordance;
    if (pt === null && cur === null) return;
    if (
      pt &&
      cur &&
      Math.abs(pt.x - cur.x) < 1e-4 &&
      Math.abs(pt.y - cur.y) < 1e-4
    ) {
      return;
    }
    this.bezierToggleAffordance = pt ? { x: pt.x, y: pt.y } : null;
    this.refreshBezierToggleAffordance();
  }

  /** Build (or rebuild) the bezier-toggle glyph: the two dots — one filled,
   *  one hollow — that the smooth/sharp button wears in the sub-toolbar
   *  (`src/icons/smooth-toggle.svg`), drawn beside the cursor (#147).
   *
   *  It is the toolbar's own icon on purpose: the button and the cursor are
   *  saying the same thing, and a second invented glyph for it would be one
   *  more thing to learn. It sits down-right of the hotspot with a gap, so the
   *  arrow never covers it. */
  refreshBezierToggleAffordance(): void {
    if (this.bezierToggleAffordanceItem) {
      this.bezierToggleAffordanceItem.remove();
    }
    this.bezierToggleAffordanceItem = null;
    const at = this.bezierToggleAffordance;
    if (!at) return;
    this.scope.activate();
    const zoom = this.scope.view.zoom || 1;
    const P = this.scope.Point;
    // Screen-constant (project mm = px / zoom) so the glyph is the same size
    // at every zoom. 80° from horizontal puts it almost straight BELOW the
    // hotspot, a few px right — following the pointer's own shape, since the
    // arm points down and a glyph out on the diagonal reads as drifting away
    // from it. The distance is set by the cursor bitmap: the hand is around
    // 22 px tall from its hotspot, and 80° of 30 px clears that vertically
    // (~29 px down) whatever the horizontal offset works out to.
    const dist = 30 / zoom;
    const rad = (80 * Math.PI) / 180;
    const centre = new P(
      at.x + Math.cos(rad) * dist,
      at.y + Math.sin(rad) * dist,
    );
    const r = 3.4 / zoom;
    const spread = 5.4 / zoom;
    const key = 1 / zoom;
    // White with a black keyline, the way a cursor is drawn — it has to stay
    // legible over leather, mat, a construction line or anything else that
    // happens to be underneath, and unlike the cursor it can't be re-tinted
    // per background without the colour itself becoming a signal it doesn't
    // mean anything by.
    const white = new this.scope.Color(1, 1, 1);
    const black = new this.scope.Color(0, 0, 0);
    const group = new this.scope.Group();
    group.data = { kind: "bezier-toggle-affordance", ephemeral: true };
    const tag = () => ({ kind: "bezier-toggle-affordance", ephemeral: true });
    // Filled dot (a corner): a black disc with a white one on top, so the
    // keyline shows as a ring around the white.
    const left = centre.subtract(new P(spread, 0));
    const filledKey = new this.scope.Path.Circle(left, r + key);
    filledKey.fillColor = black;
    filledKey.data = tag();
    group.addChild(filledKey);
    const filled = new this.scope.Path.Circle(left, r);
    filled.fillColor = white;
    filled.data = tag();
    group.addChild(filled);
    // Hollow dot (a curve): a thick black ring with a thinner white ring over
    // it, which leaves the black showing on both edges of the white.
    const right = centre.add(new P(spread, 0));
    const hollowKey = new this.scope.Path.Circle(right, r);
    hollowKey.strokeColor = black;
    hollowKey.strokeWidth = 3.6;
    hollowKey.strokeScaling = false;
    hollowKey.fillColor = null;
    hollowKey.data = tag();
    group.addChild(hollowKey);
    const hollow = new this.scope.Path.Circle(right, r);
    hollow.strokeColor = white;
    hollow.strokeWidth = 1.6;
    hollow.strokeScaling = false;
    hollow.fillColor = null;
    hollow.data = tag();
    group.addChild(hollow);
    this.bezierToggleAffordanceItem = group;
  }

  /** Set / clear the currently-hovered delete target while the
   *  cursor is over (or leaves) a deletable thing in delete mode. */
  setConstructionDeleteHover(spec: ConstructionDeleteHoverSpec | null): void {
    this.constructionDeleteHover = spec;
    this.refreshConstructionDeleteHover();
  }

  /** Build (or rebuild) the red overlay for whichever delete
   *  target the cursor currently hovers. Called from
   *  `setConstructionDeleteHover` and from the end of `render` so
   *  the overlay survives full scene rebuilds. */
  refreshConstructionDeleteHover(): void {
    if (this.constructionDeleteHoverItem) {
      this.constructionDeleteHoverItem.remove();
    }
    this.constructionDeleteHoverItem = null;
    const spec = this.constructionDeleteHover;
    if (!spec) {
      this.releaseConstructionHoverFrameLoopIfIdle();
      return;
    }
    const item = this.getConstructionLineItem(spec.constructionId);
    if (!item) return;
    const cp = item as paper.CompoundPath;
    const subpaths: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [item as unknown as paper.Path];
    this.scope.activate();
    const RED = new this.scope.Color(cssVar("--construction-broken"));
    if (spec.kind === "prune" || spec.kind === "offset-segment") {
      // The geometry arrives pre-built (a partial slice of a curve, or
      // of a whole derived path), so trace it straight from pathData
      // rather than from indices. An offset stretch can span several
      // subpaths' worth of runs, so it parses as a CompoundPath.
      let overlay: paper.Item | null = null;
      try {
        overlay = new this.scope.CompoundPath({
          pathData: spec.pathData,
          insert: true,
        });
      } catch {
        return;
      }
      overlay.data = { kind: "construction-delete-hover", ephemeral: true };
      overlay.strokeColor = RED;
      overlay.strokeWidth = 2.6;
      overlay.strokeScaling = false;
      overlay.fillColor = null;
      overlay.dashArray = [6, 4];
      overlay.dashOffset = 0;
      this.constructionDeleteHoverItem = overlay;
      this.ensureConstructionHoverFrameLoop();
      return;
    }
    const sub = subpaths[spec.pathIndex];
    if (!sub) return;
    // The ephemeral flag keeps the overlay invisible to
    // `Scene.hitTest`. Without it, the cursor lands on the red
    // overlay → hitTest walks up looking for a recognised `kind`,
    // finds none → returns `{}` → refreshEditCursor sees no
    // target → setConstructionDeleteHover(null) → overlay
    // disappears → next mouse move catches the underlying handle
    // again → overlay reappears → flicker.
    const HOVER_DATA = { kind: "construction-delete-hover", ephemeral: true };
    if (spec.kind === "segment") {
      const curve = sub.curves[spec.curveIndex];
      if (!curve) return;
      const overlay = new this.scope.Path();
      overlay.data = { ...HOVER_DATA };
      overlay.moveTo(curve.point1);
      overlay.cubicCurveTo(
        curve.point1.add(curve.handle1),
        curve.point2.add(curve.handle2),
        curve.point2,
      );
      overlay.strokeColor = RED;
      overlay.strokeWidth = 2.6;
      overlay.strokeScaling = false;
      overlay.fillColor = null;
      overlay.dashArray = [6, 4];
      overlay.dashOffset = 0;
      this.constructionDeleteHoverItem = overlay;
      this.ensureConstructionHoverFrameLoop();
      return;
    }
    if (spec.kind === "line") {
      // Whole-LINE highlight (every subpath, not just the one the
      // cursor's currently on) — the click deletes the entire
      // derived line, so the marching-ants have to cover the whole
      // line or a multi-subpath offset would leave parts of itself
      // unmarked. Same dash pattern as the per-segment delete
      // overlay so the "delete intent" idiom is consistent — no
      // halo behind it.
      const group = new this.scope.Group();
      group.data = { ...HOVER_DATA };
      for (const child of subpaths) {
        const overlay = child.clone() as paper.Path;
        overlay.data = { ...HOVER_DATA };
        overlay.strokeColor = RED;
        overlay.strokeWidth = 2.6;
        overlay.strokeScaling = false;
        overlay.fillColor = null;
        overlay.dashArray = [6, 4];
        overlay.dashOffset = 0;
        group.addChild(overlay);
      }
      this.constructionDeleteHoverItem = group;
      this.ensureConstructionHoverFrameLoop();
      return;
    }
    const seg = sub.segments[spec.segmentIndex];
    if (!seg) return;
    if (spec.kind === "anchor") {
      const overlay = new this.scope.Path.Circle(seg.point, 1.8);
      const fill = new this.scope.Color(cssVar("--construction-broken"));
      fill.alpha = 0.35;
      overlay.fillColor = fill;
      overlay.strokeColor = RED;
      overlay.strokeWidth = 1.8;
      overlay.strokeScaling = false;
      overlay.data = { ...HOVER_DATA };
      this.constructionDeleteHoverItem = overlay;
      return;
    }
    // kind === "handle" — highlight both the tangent line AND the
    // tip dot in red, since both are now hit targets for the
    // handle-delete gesture (the line shares the dot's `data`
    // tag).
    const handle = spec.side === "in" ? seg.handleIn : seg.handleOut;
    const tip = seg.point.add(handle);
    const group = new this.scope.Group();
    group.data = { ...HOVER_DATA };
    const tangent = new this.scope.Path({ segments: [seg.point, tip] });
    tangent.strokeColor = RED;
    tangent.strokeWidth = 2.0;
    tangent.strokeScaling = false;
    tangent.fillColor = null;
    // Children also tagged so the per-item ephemeral check inside
    // paper's hitTest skips them too.
    tangent.data = { ...HOVER_DATA };
    group.addChild(tangent);
    const tipDot = new this.scope.Path.Circle(tip, 1.4);
    const fill = new this.scope.Color(cssVar("--construction-broken"));
    fill.alpha = 0.35;
    tipDot.fillColor = fill;
    tipDot.strokeColor = RED;
    tipDot.strokeWidth = 1.4;
    tipDot.strokeScaling = false;
    tipDot.data = { ...HOVER_DATA };
    group.addChild(tipDot);
    this.constructionDeleteHoverItem = group;
  }
}
